import { AuthConfig, AuthService, LoginMethods } from '@pw-tech/web-sdk';
import { CameFrom, isLocalHost } from 'src/constants/constants';
import { getQueryParams } from 'src/usePlayerParams';

import { logger } from './logger/loggerService';
import { getClientId, isWEBSDKSupported } from './utils';

const getCookieKey = () => {
  const { came_from, cameFrom } = getQueryParams();
  const requestCameFrom = came_from || cameFrom;
  switch (requestCameFrom) {
    case CameFrom.MED_ED:
      return 'TOKEN_CONTEXT_MEDED';
    case CameFrom.SARRTHI_IAS:
      return 'TOKEN_CONTEXT_SARRTHI_IAS';
    case CameFrom.XYLEM:
      return 'TOKEN_CONTEXT_XYLEM';
    default:
      return 'TOKEN_CONTEXT_SARRTHI_IAS';
  }
};

export const defaultConfig: AuthConfig = {
  organizationId: `${getClientId()}`,
  apiBaseUrl: `${process.env.REACT_APP_PP_API_BACKEND}`,
  loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
  localStorageFallback: true,
  clientId: 'system-admin',
  clientSecret: `${process.env.REACT_APP_CLIENT_SECRET}`,
  contextIdentifier: getCookieKey(),
  loginPageUrl: isLocalHost() ? '/watch/' : '/',
  debugMode: isLocalHost(),
};

class PWWebSDKService {
  private static instance: PWWebSDKService;
  public webSDK: AuthService | null = null;

  private constructor() {
    // Initialize the SDK only if not in PP Admin or inside an iframe
    if (isWEBSDKSupported()) {
      this.webSDK = AuthService.getInstance(defaultConfig);

      // Set global for legacy usage
      if (typeof window !== 'undefined') {
        window.PWWebSDK = this.webSDK;
      }
    } else {
      // Do not initialize SDK in other contexts
      this.webSDK = null;
    }
  }

  public static getInstance(): AuthService | null {
    if (!PWWebSDKService.instance) {
      PWWebSDKService.instance = new PWWebSDKService();
    }
    return PWWebSDKService.instance.webSDK;
  }
}

// Create and export the singleton SDK instance (may be null)
const PwWebSDKInstance = PWWebSDKService.getInstance();
export default PwWebSDKInstance;

const checkInitApp = () => {
  if (typeof window !== 'undefined' && window?.initPWAuthWebSDK) {
    const propConfig = {
      flow: 'auth',
      webSDK: PwWebSDKInstance,
      renderType: 'modal',
      firebaseRemoteConfig: {
        enable_v2_auth_flow: true,
      },
      logFirebaseEvent: () => {},
      skipCohortSelection: true,
      details: {
        batchSlug: '',
        pageName: '',
        pageReferrer: '',
        pageReferrerUrl: '',
        pageReferrerType: '',
        pageReferrerCategory: '',
        businessUnit: '',
        referredBy: '',
        planId: '',
        parentURL: window.location.origin,
        redirectDirectlyAfterLogin: 'watch/' + window.location.search,
        onCloseModalCallBack: () => {
          console.log('onModal close', 'onCloseModalCallBack', 'authFlow');
        },
        themeFor: '',
      },
      ctaName: 'login',
      cameFrom: 'login',
    };
    window?.initPWAuthWebSDK(propConfig);
  }
};

let hasCalledCheckInitApp = false; // Add this at the top of your file

export const tryCheckInitApp = (interval = 500) => {
  if (hasCalledCheckInitApp) return;

  hasCalledCheckInitApp = true;

  const attempt = () => {
    const authFlowDiv = document.getElementById('pw_auth-flow');
    const hasAuthFlowChildren = !!(
      authFlowDiv &&
      authFlowDiv.children &&
      authFlowDiv.children.length === 0
    );
    const hasSDK = !!(PwWebSDKInstance && typeof window.initPWAuthWebSDK === 'function');

    // stop condition: both SDK init function present and auth flow DOM has children
    if (hasSDK && hasAuthFlowChildren) {
      try {
        checkInitApp();
      } catch (err) {
        logger.error('AuthSDK Error: ', err);
      } finally {
        clearInterval(attemptInterval);
        hasCalledCheckInitApp = false;
      }
      return;
    }
  };

  // run immediately and then on interval
  attempt();
  const attemptInterval = window.setInterval(attempt, interval);
};
